import React, { useId, useState } from 'react';
import { Box, Button, ButtonBase, IconButton, MenuItem, Popover, Select } from '@mui/material';
import {
  CalendarTodayOutlined as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  KeyboardArrowDownRounded as ChevronDownIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../../i18n/locale-format';

// Same "YYYY-MM-DDTHH:mm" local-time string a datetime-local input uses, so forms can swap it in.
interface DateTimeFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}

type Parts = { y: number; m: number; d: number; hh: number; mm: number };

const pad = (n: number) => String(n).padStart(2, '0');
// Monday-first short weekday names in the active locale (2024-01-01 was a Monday).
const weekdayLabels = (locale: string): string[] =>
  Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: 'narrow' }));
// vi-VN month names are lowercase ("tháng 1 năm 2024"); capitalise for the calendar heading.
const capitalize = (s: string) => s.charAt(0).toLocaleUpperCase() + s.slice(1);
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const FIVE_MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

// Parsed by hand: new Date('YYYY-MM-DDTHH:mm') is local in browsers today, but this avoids relying on it.
const parse = (value: string): Parts => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) {
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth(), d: now.getDate(), hh: now.getHours(), mm: 0 };
  }
  const [y, m, d, hh, mm] = match.slice(1).map(Number);
  return { y, m: m - 1, d, hh, mm };
};

const serialize = (p: Parts) => `${p.y}-${pad(p.m + 1)}-${pad(p.d)}T${pad(p.hh)}:${pad(p.mm)}`;

const sameDay = (a: Date, p: Pick<Parts, 'y' | 'm' | 'd'>) => a.getFullYear() === p.y && a.getMonth() === p.m && a.getDate() === p.d;

// Six Monday-first weeks covering the month, padded with the neighbouring months' days.
const monthGrid = (y: number, m: number): Date[] => {
  const offset = (new Date(y, m, 1).getDay() + 6) % 7;
  return Array.from({ length: 42 }, (_, i) => new Date(y, m, 1 - offset + i));
};

const timeSelectSx = { height: 34, minWidth: 72, '& .MuiSelect-select': { py: 0.5 } } as const;

export const DateTimeField = ({ label, value, onChange, required }: DateTimeFieldProps) => {
  const { t } = useTranslation('layout');
  const locale = intlLocale();
  const labelId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const parts = parse(value);
  const [view, setView] = useState({ y: parts.y, m: parts.m });
  const today = new Date();

  const update = (patch: Partial<Parts>) => onChange(serialize({ ...parts, ...patch }));

  const pickDay = (date: Date) => {
    update({ y: date.getFullYear(), m: date.getMonth(), d: date.getDate() });
    setView({ y: date.getFullYear(), m: date.getMonth() });
  };

  const shiftMonth = (delta: number) => {
    const next = new Date(view.y, view.m + delta, 1);
    setView({ y: next.getFullYear(), m: next.getMonth() });
  };

  const open = (e: React.MouseEvent<HTMLElement>) => {
    setView({ y: parts.y, m: parts.m });
    setAnchor(e.currentTarget);
  };

  // Keep an existing off-step minute (e.g. :03) selectable instead of silently rounding it.
  const minutes = FIVE_MINUTES.includes(parts.mm) ? FIVE_MINUTES : [...FIVE_MINUTES, parts.mm].sort((a, b) => a - b);
  const display = `${new Date(parts.y, parts.m, parts.d).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })} · ${pad(parts.hh)}:${pad(parts.mm)}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box id={labelId} component="span" sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.4, mb: '6px' }}>
        {label}
        {required && <Box component="span" sx={{ color: 'var(--danger)' }}> *</Box>}
      </Box>
      <ButtonBase
        aria-labelledby={labelId}
        aria-haspopup="dialog"
        onClick={open}
        sx={{
          height: 40, px: 1.5, gap: 1, justifyContent: 'flex-start', fontSize: 14, color: 'var(--fg)',
          border: '1px solid', borderColor: anchor ? 'var(--primary)' : 'var(--border)', borderRadius: '8px',
          backgroundColor: 'var(--card)', boxShadow: anchor ? '0 0 0 3px var(--accent-bg)' : 'none',
          transition: 'border-color .15s, box-shadow .15s',
          '&:hover': { borderColor: anchor ? 'var(--primary)' : 'var(--muted)' },
          '&.Mui-focusVisible': { borderColor: 'var(--primary)', boxShadow: '0 0 0 3px var(--accent-bg)' },
        }}
      >
        <CalendarIcon sx={{ fontSize: 16, color: 'var(--muted)' }} />
        <Box component="span" sx={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{display}</Box>
        <ChevronDownIcon sx={{ fontSize: 18, color: 'var(--muted)' }} />
      </ButtonBase>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': t('dateTimeField.dialogLabel', { label: label.toLowerCase() }),
            sx: { mt: 0.5, p: 1.5, width: 296, border: '1px solid var(--border)', borderRadius: '12px', boxShadow: '0 12px 32px rgba(0,0,0,.14)' },
          } as object,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <IconButton size="small" aria-label={t('dateTimeField.previousMonth')} onClick={() => shiftMonth(-1)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Box sx={{ fontSize: 14, fontWeight: 600 }}>
            {capitalize(new Date(view.y, view.m, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' }))}
          </Box>
          <IconButton size="small" aria-label={t('dateTimeField.nextMonth')} onClick={() => shiftMonth(1)}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {weekdayLabels(locale).map((w, i) => (
            <Box key={i} sx={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)', textAlign: 'center', py: 0.5 }}>{w}</Box>
          ))}
          {monthGrid(view.y, view.m).map(date => {
            const selected = sameDay(date, parts);
            const isToday = sameDay(today, { y: date.getFullYear(), m: date.getMonth(), d: date.getDate() });
            const outside = date.getMonth() !== view.m;
            return (
              <ButtonBase
                key={date.toISOString()}
                onClick={() => pickDay(date)}
                aria-pressed={selected}
                aria-label={date.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                sx={{
                  height: 36, borderRadius: '8px', fontSize: 13, fontVariantNumeric: 'tabular-nums',
                  fontWeight: selected || isToday ? 600 : 400,
                  color: selected ? '#fff' : isToday ? 'var(--primary)' : outside ? 'var(--muted)' : 'var(--fg)',
                  opacity: outside && !selected ? 0.55 : 1,
                  background: selected ? 'var(--grad)' : 'transparent',
                  boxShadow: isToday && !selected ? 'inset 0 0 0 1px var(--border)' : 'none',
                  '&:hover': { backgroundColor: selected ? undefined : 'var(--muted-bg)' },
                  '&.Mui-focusVisible': { outline: '2px solid var(--primary)', outlineOffset: 1 },
                }}
              >
                {date.getDate()}
              </ButtonBase>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, pt: 1.5, borderTop: '1px solid var(--border)' }}>
          <Box component="span" sx={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{t('dateTimeField.time')}</Box>
          <Select size="small" value={parts.hh} onChange={e => update({ hh: Number(e.target.value) })} inputProps={{ 'aria-label': t('dateTimeField.hour') }} sx={timeSelectSx}>
            {HOURS.map(h => <MenuItem key={h} value={h}>{pad(h)}</MenuItem>)}
          </Select>
          <Box component="span" sx={{ color: 'var(--muted)' }}>:</Box>
          <Select size="small" value={parts.mm} onChange={e => update({ mm: Number(e.target.value) })} inputProps={{ 'aria-label': t('dateTimeField.minute') }} sx={timeSelectSx}>
            {minutes.map(m => <MenuItem key={m} value={m}>{pad(m)}</MenuItem>)}
          </Select>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
          <Button size="small" variant="text" onClick={() => pickDay(today)}>{t('dateTimeField.today')}</Button>
          <Button size="small" variant="contained" onClick={() => setAnchor(null)}>{t('dateTimeField.done')}</Button>
        </Box>
      </Popover>
    </Box>
  );
};
